import { pan, interactionState } from './viewportState.js';
import type { NodeEditorController } from './nodeEditor.js';
import { clamp } from '../math/scalar.js';

interface WheelBuffer {
  dx: number;
  dy: number;
  zoom: number;
  mx: number;
  my: number;
  pending: boolean;
}

function isInsideGraphNode(target: EventTarget | null): boolean {
  return target instanceof HTMLElement && Boolean(target.closest('.node'));
}

export function initViewportInteractions(
  graphArea: HTMLElement,
  deps: { hasPendingConnection: () => boolean; nodeEditor: Pick<NodeEditorController, 'refreshGraphView' | 'setSelectedNode'> },
): void {
  let isPanning = false;
  let panStart = { x: 0, y: 0 };
  const wheelBuf: WheelBuffer = { dx: 0, dy: 0, zoom: 1, mx: 0, my: 0, pending: false };
  const activePointers = new Map<number, { x: number; y: number }>();
  let lastTouch: { mid: { x: number; y: number }; dist: number } | null = null;

  const flushWheel = (): void => {
    wheelBuf.pending = false;
    const { dx, dy, zoom, mx, my } = wheelBuf;
    wheelBuf.dx = 0;
    wheelBuf.dy = 0;
    wheelBuf.zoom = 1;

    if (zoom !== 1) {
      const newScale = clamp(pan.scale * zoom, 0.15, 4);
      const f = newScale / pan.scale;
      pan.x = mx - (mx - pan.x) * f;
      pan.y = my - (my - pan.y) * f;
      pan.scale = newScale;
    }
    pan.x -= dx;
    pan.y -= dy;
    deps.nodeEditor.refreshGraphView();
  };

  const touchMidAndDist = (): { mid: { x: number; y: number }; dist: number } => {
    const pts = [...activePointers.values()];
    const mid = { x: (pts[0].x + pts[1].x) / 2, y: (pts[0].y + pts[1].y) / 2 };
    const dist = Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y);
    return { mid, dist };
  };

  graphArea.addEventListener('mousedown', e => {
    if (isInsideGraphNode(e.target)) {
      return;
    }
    if (e.button === 1 || (e.button === 0 && !e.ctrlKey && !interactionState.draggingNode && !deps.hasPendingConnection())) {
      isPanning = true;
      panStart = { x: e.clientX - pan.x, y: e.clientY - pan.y };
      graphArea.classList.add('panning');
      deps.nodeEditor.setSelectedNode(null);
    }
  });

  window.addEventListener('mousemove', e => {
    if (!isPanning) return;
    pan.x = e.clientX - panStart.x;
    pan.y = e.clientY - panStart.y;
    deps.nodeEditor.refreshGraphView();
  });

  window.addEventListener('mouseup', () => {
    if (!isPanning) return;
    isPanning = false;
    graphArea.classList.remove('panning');
  });

  graphArea.addEventListener('wheel', e => {
    e.preventDefault();
    const rect = graphArea.getBoundingClientRect();
    wheelBuf.mx = e.clientX - rect.left;
    wheelBuf.my = e.clientY - rect.top;

    if (e.deltaMode !== 0) {
      wheelBuf.zoom *= e.deltaY > 0 ? 0.88 : 1.14;
    } else if (e.ctrlKey) {
      const clamped = clamp(e.deltaY * 0.02, -0.25, 0.25);
      wheelBuf.zoom *= (1 - clamped);
      wheelBuf.dx += e.deltaX;
    } else {
      if (e.shiftKey && e.deltaX === 0) {
        wheelBuf.dx += e.deltaY;
      } else {
        wheelBuf.dx += e.deltaX;
        wheelBuf.dy += e.deltaY;
      }
    }

    if (!wheelBuf.pending) {
      wheelBuf.pending = true;
      requestAnimationFrame(flushWheel);
    }
  }, { passive: false });

  graphArea.addEventListener('pointerdown', e => {
    if (e.pointerType !== 'touch') return;
    activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    graphArea.setPointerCapture(e.pointerId);
    if (activePointers.size === 2) {
      lastTouch = touchMidAndDist();
      isPanning = false;
      interactionState.draggingNode = null;
      graphArea.classList.remove('panning');
    }
  });

  graphArea.addEventListener('pointermove', e => {
    if (e.pointerType !== 'touch' || !activePointers.has(e.pointerId)) return;
    activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (activePointers.size !== 2 || !lastTouch) return;

    const rect = graphArea.getBoundingClientRect();
    const curr = touchMidAndDist();
    const newScale = clamp(pan.scale * curr.dist / lastTouch.dist, 0.15, 4);
    const f = newScale / pan.scale;
    const midX = curr.mid.x - rect.left;
    const midY = curr.mid.y - rect.top;
    pan.x = midX - (midX - pan.x) * f;
    pan.y = midY - (midY - pan.y) * f;
    pan.scale = newScale;
    pan.x += curr.mid.x - lastTouch.mid.x;
    pan.y += curr.mid.y - lastTouch.mid.y;
    lastTouch = curr;
    deps.nodeEditor.refreshGraphView();
  });

  const onTouchEnd = (e: PointerEvent): void => {
    if (e.pointerType !== 'touch') return;
    activePointers.delete(e.pointerId);
    if (activePointers.size < 2) lastTouch = null;
  };

  graphArea.addEventListener('pointerup', onTouchEnd);
  graphArea.addEventListener('pointercancel', onTouchEnd);
  graphArea.addEventListener('dblclick', e => {
    if (e.target !== graphArea && !(e.target as HTMLElement).closest('#graph-canvas-wrapper')) return;
  });
}
